import React, { useState, useEffect } from 'react';
import { 
    Grid, 
    Typography, 
    Box, 
    CircularProgress, 
    Alert,
    Pagination,
    Chip
} from '@mui/material';
import { userRequest } from '../../requestMethods';
import AlgorithmicProblemCard from './AlgorithmicProblemCard';
import PageContainer from '../layout/PageContainer';

const TitleWithBackground = ({ title, subtitle }) => {
    return (
        <Box 
            sx={{
                background: (theme) => theme.palette.grey[100],
                padding: (theme) => theme.spacing(3),
                marginBottom: (theme) => theme.spacing(4),
                borderRadius: (theme) => theme.shape.borderRadius,
                textAlign: 'center'
            }}
        >
            <Typography 
                variant="h2" 
                sx={{
                    color: 'text.primary',
                    fontWeight: 'bold',
                    fontSize: {
                        xs: '1.8rem',
                        sm: '2.2rem',
                        md: '2.5rem'
                    },
                    marginBottom: 1
                }}
            >
                {title}
            </Typography>
            {subtitle && (
                <Typography 
                    variant="h6" 
                    sx={{
                        color: 'text.secondary',
                        fontSize: {
                            xs: '1rem',
                            sm: '1.1rem',
                            md: '1.2rem'
                        }
                    }}
                >
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
};

const AlgorithmicProblems = () => {
    const [problems, setProblems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [difficultyFilter, setDifficultyFilter] = useState('');
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalProblems, setTotalProblems] = useState(0);
    const pageSize = 8; // Fixed page size for public problems

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1); // Reset to first page when search term changes
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    // Fetch problems when page, search, or difficulty filter changes
    useEffect(() => {
        fetchProblems();
    }, [currentPage, debouncedSearchTerm, difficultyFilter]);

    const fetchProblems = async () => {
        try {
            setLoading(true);
            setError('');
            
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: pageSize.toString(),
                ...(debouncedSearchTerm.trim() && { search: debouncedSearchTerm.trim() }),
                ...(difficultyFilter && { difficulty: difficultyFilter })
            });
            
            const response = await userRequest.get(`/algorithmic-problem?${params}`);
            
            setProblems(response.data.problems);
            setTotalPages(response.data.pagination.totalPages);
            setTotalProblems(response.data.pagination.totalProblems);
            
        } catch (error) {
            console.error('Error fetching problems:', error);
            setError('Failed to load problems. Please try again later.');
            setProblems([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDifficultyFilter = (difficulty) => {
        setDifficultyFilter(difficulty === difficultyFilter ? '' : difficulty);
        setCurrentPage(1);
    };

    return (
        <PageContainer>
            <TitleWithBackground 
                title="Algorithmic Problems"
                subtitle="Practice coding problems and learn English"
            />

            {/* Difficulty Filter */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                    label="All"
                    onClick={() => handleDifficultyFilter('')}
                    color={difficultyFilter === '' ? 'primary' : 'default'}
                    variant={difficultyFilter === '' ? 'filled' : 'outlined'}
                />
                <Chip
                    label="Easy"
                    onClick={() => handleDifficultyFilter('easy')}
                    color={difficultyFilter === 'easy' ? 'success' : 'default'}
                    variant={difficultyFilter === 'easy' ? 'filled' : 'outlined'}
                />
                <Chip
                    label="Medium"
                    onClick={() => handleDifficultyFilter('medium')}
                    color={difficultyFilter === 'medium' ? 'warning' : 'default'}
                    variant={difficultyFilter === 'medium' ? 'filled' : 'outlined'}
                />
                <Chip
                    label="Hard"
                    onClick={() => handleDifficultyFilter('hard')}
                    color={difficultyFilter === 'hard' ? 'error' : 'default'}
                    variant={difficultyFilter === 'hard' ? 'filled' : 'outlined'}
                />
            </Box>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Loading State */}
            {loading && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <CircularProgress size={60} />
                </Box>
            )}

            {/* Problems Grid */}
            {!loading && !error && problems.length > 0 && (
                <>
                    <Grid container spacing={3}>
                        {problems.map((problem) => (
                            <Grid item key={problem._id} xs={12} sm={6} md={4} lg={3}>
                                <AlgorithmicProblemCard problem={problem} />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box display="flex" justifyContent="center" sx={{ mt: 4, mb: 2 }}>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </>
            )}

            {/* Empty State */}
            {!loading && !error && problems.length === 0 && (
                <Box 
                    display="flex" 
                    flexDirection="column" 
                    alignItems="center" 
                    justifyContent="center" 
                    minHeight="300px"
                    textAlign="center"
                >
                    <Typography variant="h5" color="text.secondary" gutterBottom>
                        {searchTerm || difficultyFilter ? 'No problems match your filters' : 'No problems available yet'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {searchTerm || difficultyFilter
                            ? 'Try adjusting your filters or browse all problems' 
                            : 'Check back later for new problems'
                        }
                    </Typography>
                </Box>
            )}
        </PageContainer>
    );
};

export default AlgorithmicProblems;

